import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export type TerminalLevel = 'info' | 'ok' | 'warn' | 'err' | 'flash' | 'serial';

export interface TerminalLine {
  id: number;
  level: TerminalLevel;
  text: string;
  ts: string;
}

export type FlashPhase = 'idle' | 'generating' | 'compiling' | 'flashing' | 'monitoring' | 'error';
export type SerialState = 'disconnected' | 'connecting' | 'connected';
export type TerminalTab = 'output' | 'serial';
export type AppView = 'home' | 'studio' | 'admin' | 'org';

/** A project handed from the home page to the studio canvas on next mount. */
export interface PendingProject {
  name: string;
  /** Serialized workspace JSON, or null for a brand-new empty project. */
  data: string | null;
}

export interface AiToolCall {
  name: string;
  ok: boolean;
  summary: string;
}

export interface AiMessage {
  role: 'user' | 'assistant';
  text: string;
  toolCalls?: AiToolCall[];
  isError?: boolean;
}

export interface Settings {
  apiKey: string;
  model: string;
  fqbn: string;
  flashBaud: number;
  serialBaud: number;
  compileUrl: string;
}

export interface AuthUser {
  id: string;
  /** Display name: email for the main admin, username for organization accounts. */
  name: string;
  email: string | null;
  username: string | null;
  orgId: string | null;
  orgName: string | null;
  role: 'main_admin' | 'org_admin' | 'org_manager' | 'member';
  isAdmin: boolean;
  quotaMb: number;
}

export const DEFAULT_MODEL = 'gemini-3.5-flash-lite';
export const DEFAULT_COMPILE_URL = 'http://localhost:8788';

const DEFAULT_SETTINGS: Settings = {
  apiKey: '',
  model: DEFAULT_MODEL,
  fqbn: 'esp32:esp32:esp32',
  flashBaud: 921600,
  serialBaud: 115200,
  compileUrl: DEFAULT_COMPILE_URL,
};

const TERMINAL_CAP = 2000;

let lineId = 0;

function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/* ------------------------------------------------------------------ */
/* One-time migration from pre-rebrand storage keys                    */
/* ------------------------------------------------------------------ */

function migrateLegacyKeys(): void {
  try {
    const pairs: [string, string][] = [
      ['ebs.settings', 'spblock.settings'],
      ['ebs.workspace', 'spblock.workspace'],
    ];
    for (const [from, to] of pairs) {
      const legacy = localStorage.getItem(from);
      if (legacy !== null) {
        if (localStorage.getItem(to) === null) localStorage.setItem(to, legacy);
        localStorage.removeItem(from);
      }
    }
  } catch {
    /* storage unavailable — ignore */
  }
}
if (typeof localStorage !== 'undefined') migrateLegacyKeys();

/* ------------------------------------------------------------------ */
/* Store                                                               */
/* ------------------------------------------------------------------ */

interface StoreState {
  /* persisted */
  settings: Settings;
  projectName: string;
  auth: { token: string | null; user: AuthUser | null };
  setSetting: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  setProjectName: (name: string) => void;
  setAuth: (token: string | null, user: AuthUser | null) => void;

  /* terminal */
  lines: TerminalLine[];
  activeTab: TerminalTab;
  terminalCollapsed: boolean;
  pushLine: (level: TerminalLevel, text: string) => void;
  clearLines: () => void;
  setActiveTab: (tab: TerminalTab) => void;
  toggleTerminal: () => void;

  /* flash + serial */
  flashPhase: FlashPhase;
  flashProgress: number | null;
  serialState: SerialState;
  setFlashPhase: (phase: FlashPhase) => void;
  setFlashProgress: (pct: number | null) => void;
  setSerialState: (state: SerialState) => void;

  /* chrome */
  aiOpen: boolean;
  toggleAi: () => void;
  settingsOpen: boolean;
  codeModalOpen: boolean;
  view: AppView;
  pendingProject: PendingProject | null;
  setSettingsOpen: (open: boolean) => void;
  setCodeModalOpen: (open: boolean) => void;
  setView: (view: AppView) => void;
  setPendingProject: (project: PendingProject | null) => void;
  resetForNewProject: () => void;

  /* cloud autosave status */
  saveStatus: 'idle' | 'dirty' | 'saving' | 'saved' | 'error';
  lastSavedAt: string | null;
  setSaveStatus: (status: StoreState['saveStatus']) => void;
  setLastSavedAt: (iso: string | null) => void;
  setOrgName: (name: string) => void;
}


/* ------------------------------------------------------------------ */
/* Cross-tab session sync                                              */
/* ------------------------------------------------------------------ */
/* Single-active-session + multiple tabs: when any tab signs in, the newer
   session is written to localStorage and every other tab adopts it here. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== 'spblock.settings' || !event.newValue) return;
    try {
      const parsed = JSON.parse(event.newValue) as {
        state?: { auth?: { token: string | null; user: StoreState['auth']['user'] } };
      };
      const incoming = parsed.state?.auth;
      if (incoming && useStore.getState().auth.token !== incoming.token) {
        useStore.setState({ auth: incoming });
      }
    } catch {
      /* ignore malformed storage writes */
    }
  });
}

export const useStore = create<StoreState>()(
  persist(
    (set) => ({
      settings: DEFAULT_SETTINGS,
      projectName: 'untitled',
      auth: { token: null, user: null },
      setSetting: (key, value) =>
        set((s) => ({ settings: { ...s.settings, [key]: value } })),
      setProjectName: (name) => set({ projectName: name }),
      setAuth: (token, user) => set({ auth: { token, user } }),

      lines: [],
      activeTab: 'output',
      terminalCollapsed: true,
      pushLine: (level, text) =>
        set((s) => {
          const next = [...s.lines, { id: ++lineId, level, text, ts: stamp() }];
          return { lines: next.length > TERMINAL_CAP ? next.slice(next.length - TERMINAL_CAP) : next };
        }),
      clearLines: () => set({ lines: [] }),
      setActiveTab: (tab) => set({ activeTab: tab }),
      toggleTerminal: () => set((s) => ({ terminalCollapsed: !s.terminalCollapsed })),

      flashPhase: 'idle',
      flashProgress: null,
      serialState: 'disconnected',
      setFlashPhase: (phase) => set({ flashPhase: phase }),
      setFlashProgress: (pct) => set({ flashProgress: pct }),
      setSerialState: (state) => set({ serialState: state }),

      aiOpen: false,
      toggleAi: () => set((s) => ({ aiOpen: !s.aiOpen })),
      settingsOpen: false,
      codeModalOpen: false,
      view: 'home',
      pendingProject: null,
      setSettingsOpen: (open) => set({ settingsOpen: open }),
      setCodeModalOpen: (open) => set({ codeModalOpen: open }),
      setView: (view) => set({ view }),
      setPendingProject: (project) => set({ pendingProject: project }),
      resetForNewProject: () =>
        set({ projectName: 'untitled', lines: [], flashPhase: 'idle', flashProgress: null, saveStatus: 'idle', lastSavedAt: null }),

      saveStatus: 'idle',
      lastSavedAt: null,
      setSaveStatus: (status) => set({ saveStatus: status }),
      setLastSavedAt: (iso) => set({ lastSavedAt: iso }),
      setOrgName: (name) =>
        set((s) => ({
          auth: s.auth.user ? { ...s.auth, user: { ...s.auth.user, orgName: name } } : s.auth,
        })),
    }),
    {
      name: 'spblock.settings',
      version: 2,
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as Partial<StoreState> & {
          auth?: { token: string | null; user: Record<string, unknown> | null };
        };
        // v1 → v2: reshape the persisted account to the organization model.
        if (state.auth?.user) {
          const old = state.auth.user as Record<string, unknown>;
          const role =
            old.role === 'main_admin' || old.isAdmin === true ? 'main_admin' : 'member';
          const name = (old.name as string) ?? (old.email as string) ?? 'account';
          state.auth.user = {
            id: String(old.id ?? ''),
            name,
            email: (old.email as string) ?? null,
            username: null,
            orgId: null,
            orgName: null,
            role: role as AuthUser['role'],
            isAdmin: role === 'main_admin',
            quotaMb: 2,
          };
        }
        return {
          settings: { ...DEFAULT_SETTINGS, ...(state.settings ?? {}) },
          projectName: state.projectName ?? 'untitled',
          auth: state.auth ?? { token: null, user: null },
        } as StoreState;
      },
      partialize: (s) => ({
        settings: s.settings,
        projectName: s.projectName,
        auth: s.auth,
      }),
    },
  ),
);
